import { useCallback, useMemo, useState } from 'react';

import { cn } from '@lyearn/style';
import { Box, Stack } from '@lyearn/ui';
import useDnd from '@/components/DragDrop/DropOverContainer/useDnd';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import * as ActionItem from '@/modules/tasks/features/actionItems/components/ActionItem';
import { ActionItemFragment } from '@/modules/tasks/features/actionItems/graphql/fragments/generated/ActionItem';

import KanbanDueDateModal from '../../TaskDashboard/TasksKanbanView/KanbanDueDateModal';
import { TaskGroupByEnum } from '../../TaskDashboard/types';
import { GroupedListProps } from '../types';

const LocalActionItemProvider = ({
  className,
  rowData,
  actions,
  children,
  providers,
  draggingItemRef,
  groupBy,
  disableInteractiveTooltip = false,
}: Pick<GroupedListProps, 'actions' | 'providers'> & {
  className?: string;
  rowData: ActionItemFragment;
  children: React.ReactNode;
  draggingItemRef: React.MutableRefObject<ActionItemFragment | null>;
  groupBy?: string | null;
  disableInteractiveTooltip?: boolean;
}) => {
  const [showDatePicker, setShowDatePicker] = useState(false);

  const onDropCallback = useCallback(() => {
    const movedItemId = draggingItemRef.current?.id;
    if (!movedItemId) {
      return;
    }
    switch (groupBy) {
      case TaskGroupByEnum.Status: {
        if (rowData.statusInfo) {
          actions.onStatusChange(rowData.statusInfo, movedItemId);
        }
        break;
      }
      case TaskGroupByEnum.Priority: {
        if (rowData.priority) {
          actions.onPriorityChange(rowData.priority, movedItemId);
        }
        break;
      }
      case TaskGroupByEnum.Assignee: {
        if (rowData.assignedToId) {
          actions.onAssignClick(rowData.assignedToId, movedItemId);
        }
        break;
      }
      case TaskGroupByEnum.DueDate: {
        setShowDatePicker(true);
        break;
      }
      case TaskGroupByEnum.Stage: {
        if (rowData.stageIds?.[0]) {
          const stageId = rowData.stageIds[0];
          actions.onStageChange(stageId ? [stageId] : undefined, movedItemId);
        }
        break;
      }
      default:
        break;
    }
  }, [
    draggingItemRef,
    groupBy,
    rowData.statusInfo,
    rowData.priority,
    rowData.assignedToId,
    rowData.stageIds,
    actions,
  ]);

  const { isDragOver, onDragOver, onDrop, onDragLeave } = useDnd({
    onDropCallback,
    canDrop: () => true,
  });

  const provider = useMemo(() => {
    const { integrationConfig } = rowData;

    if (integrationConfig?.providerId && providers?.length) {
      return providers.find((provider) => provider.id === integrationConfig.providerId);
    }

    return undefined;
  }, [providers, rowData]);

  const onDragStart = useCallback(() => {
    draggingItemRef.current = rowData;
  }, [draggingItemRef, rowData]);

  const {
    [FeatureFlags.EnableProjectManagementPreReleaseFeatures]: isProjectManagementPreReleaseEnabled,
  } = useFeatureFlags();

  return (
    <>
      <Stack direction="col">
        <Stack
          className={cn('w-full', className)}
          draggable={isProjectManagementPreReleaseEnabled}
          onDragLeave={onDragLeave}
          onDragOver={onDragOver}
          onDragStart={onDragStart}
          onDrop={onDrop}>
          <ActionItem.Provider
            actionItem={rowData}
            actions={actions}
            className="task-list-item group"
            disableInteractiveTooltip={disableInteractiveTooltip}
            provider={provider}>
            {children}
          </ActionItem.Provider>
        </Stack>
        {isDragOver ? (
          <Box
            className={cn(
              'drop-line absolute -mt-2 h-2 rounded-full bg-interactive-primary end-0 start-0',
            )}
          />
        ) : null}
      </Stack>
      {draggingItemRef.current && (
        <KanbanDueDateModal
          draggingItemRef={draggingItemRef}
          isOpen={showDatePicker}
          onChangeDueDate={actions.onDueDateClick}
          onClose={() => setShowDatePicker(false)}
        />
      )}
    </>
  );
};

export default LocalActionItemProvider;
